import React, { Fragment, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Chip,
    IconButton,
    Collapse,
    Grid,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';

function CategoryRow({ categoria }) {
    const [open, setOpen] = useState(false);

    return (
        <Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, bgcolor: 'grey.50' }}>
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={() => setOpen(!open)}
                        disabled={!categoria.permissoes || categoria.permissoes.length === 0}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight={600}>
                        {categoria.nome}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <Chip
                        label={categoria.quantidade_maxima}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </TableCell>
                <TableCell align="center">{categoria.periodo}</TableCell>
                <TableCell align="center">
                    <Chip
                        label={categoria.ponto_alerta}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                </TableCell>
                <TableCell align="center">
                    <Chip
                        icon={categoria.status ? <CheckCircleIcon /> : <CancelIcon />}
                        label={categoria.status ? "Ativo" : "Inativo"}
                        color={categoria.status ? "primary" : "default"}
                        size="small"
                    />
                </TableCell>
                <TableCell align="center">
                    <Typography variant="caption" color="text.secondary">
                        {categoria.permissoes?.length || 0} permissões
                    </Typography>
                </TableCell>
            </TableRow>

            {/* Permissões expandíveis */}
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, pl: 6 }}>
                            <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mb: 2 }}>
                                Permissões da Categoria
                            </Typography>

                            {categoria.permissoes?.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                <TableCell>Produto/EPI</TableCell>
                                                <TableCell align="center">Período</TableCell>
                                                <TableCell align="center">Ponto de Alerta</TableCell>
                                                <TableCell align="center">Status</TableCell>
                                                <TableCell align="center">Sobrescreve Categoria</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {categoria.permissoes.map((perm) => (
                                                <TableRow key={perm.id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {perm.produto?.nome || "—"}
                                                        </Typography>

                                                        {perm.produto?.cod_externo_filhos?.length > 0 && (
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    flexWrap: "wrap",
                                                                    gap: 0.5,
                                                                    mt: 1
                                                                }}
                                                            >
                                                                {perm.produto.cod_externo_filhos.map((codigo, index) => (
                                                                    <Chip
                                                                        key={index}
                                                                        label={codigo}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={perm.periodo} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={perm.ponto_alerta}
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            icon={(perm.produto?.status && perm.status) ?
                                                                <CheckCircleIcon /> : <CancelIcon />}
                                                            label={(perm.produto?.status && perm.status) ?
                                                                "Ativo" : "Inativo"}
                                                            color={(perm.produto?.status && perm.status) ?
                                                                "primary" : "default"}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={perm.info_categoria ? "Não" : "Sim"}
                                                            color={perm.info_categoria ? "default" : "primary"}
                                                            size="small"
                                                            variant={perm.info_categoria ? "outlined" : "filled"}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    Nenhuma permissão vinculada a esta categoria
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
}

export function GHECategoryDetails({ data }) {
    if (!data || !data.categorias || data.categorias.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="body1" color="text.secondary" align="center">
                        Nenhuma categoria vinculada a este GHE
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Estatísticas resumidas
    const totalCategorias = data.categorias.length;
    const categoriasAtivas = data.categorias.filter(cat => cat.status).length;
    const totalPermissoes = data.categorias.reduce(
        (sum, cat) => sum + (cat.permissoes?.length || 0),
        0
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* Cards de resumo */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                                Total de Categorias
                            </Typography>
                            <Typography variant="h4" color="primary">
                                {totalCategorias}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                                Categorias Ativas
                            </Typography>
                            <Typography variant="h4" color="primary">
                                {categoriasAtivas}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                                Total de Permissões
                            </Typography>
                            <Typography variant="h4" color="primary">
                                {totalPermissoes}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Tabela de categorias */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Categorias e Permissões
            </Typography>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', width: 50 }} />
                            <TableCell sx={{ color: 'white' }}>Categoria</TableCell>
                            <TableCell sx={{ color: 'white' }} align="center">Qtd. Máxima</TableCell>
                            <TableCell sx={{ color: 'white' }} align="center">Período</TableCell>
                            <TableCell sx={{ color: 'white' }} align="center">Ponto de Alerta</TableCell>
                            <TableCell sx={{ color: 'white' }} align="center">Status</TableCell>
                            <TableCell sx={{ color: 'white' }} align="center">Permissões</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.categorias.map((categoria) => (
                            <CategoryRow key={categoria.id} categoria={categoria} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}